import type { ReactNode } from 'react';

import clsxm from '@/lib/clsxm';

/**
 * A phone, drawn rather than photographed: a dark bezel, a status bar in the
 * app's own header colour (`bar`), and the app's screen below it.
 */
export default function PhoneFrame({
  bar,
  className,
  children,
}: {
  /** Tailwind classes for the status bar's background and text. */
  bar: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={clsxm(
        'relative mx-auto w-full max-w-[18.5rem] rounded-[2.75rem] bg-[#0e0e12] p-[9px] text-start shadow-[0_50px_100px_-35px_rgba(10,6,50,0.85),inset_0_0_0_1.5px_rgba(255,255,255,0.09)]',
        className,
      )}
    >
      <div className='relative overflow-hidden rounded-[2.2rem]'>
        <StatusBar className={bar} />
        {children}
      </div>
    </div>
  );
}

function StatusBar({ className }: { className: string }) {
  return (
    <div
      dir='ltr'
      className={clsxm(
        'relative flex h-9 items-center justify-between px-6 pt-1 text-[12px] font-semibold',
        className,
      )}
    >
      <span className='tabular-nums'>9:41</span>
      <span className='absolute left-1/2 top-2 h-[18px] w-[74px] -translate-x-1/2 rounded-full bg-black' />
      <span className='flex items-center gap-1'>
        <svg
          viewBox='0 0 18 12'
          className='h-[10px] w-[15px]'
          fill='currentColor'
        >
          <rect x='0' y='8' width='3' height='4' rx='1' />
          <rect x='5' y='5.5' width='3' height='6.5' rx='1' />
          <rect x='10' y='3' width='3' height='9' rx='1' />
          <rect x='15' y='0' width='3' height='12' rx='1' />
        </svg>
        <svg
          viewBox='0 0 16 12'
          className='h-[10px] w-[14px]'
          fill='currentColor'
        >
          <path d='M8 2.4c2.2 0 4.2.9 5.7 2.3l1.1-1.1A9.6 9.6 0 0 0 8 .8 9.6 9.6 0 0 0 1.2 3.6l1.1 1.1A8 8 0 0 1 8 2.4zm0 3.2c1.3 0 2.5.5 3.4 1.4l1.1-1.1A6.4 6.4 0 0 0 8 4c-1.8 0-3.4.7-4.5 1.9L4.6 7A4.8 4.8 0 0 1 8 5.6zm0 3.2c-.5 0-1 .2-1.3.6L8 10.8l1.3-1.4c-.3-.4-.8-.6-1.3-.6z' />
        </svg>
        <svg viewBox='0 0 26 12' className='h-[11px] w-[24px]' fill='none'>
          <rect
            x='0.5'
            y='0.5'
            width='22'
            height='11'
            rx='3.5'
            stroke='currentColor'
            opacity='0.4'
          />
          <rect x='2' y='2' width='17' height='8' rx='2' fill='currentColor' />
          <path
            d='M24 4v4'
            stroke='currentColor'
            strokeWidth='1.5'
            opacity='0.4'
          />
        </svg>
      </span>
    </div>
  );
}
